import { storage } from '../utils/storage';

/**
 * Network layer for the optional backend (accounts / cloud saves / leaderboard).
 *
 * The game works fully offline (LocalStorage); this only activates when a
 * server is reachable. In the static artifact there is no server, so calls
 * reject and the UI shows an offline message — the game still plays locally.
 */
const SERVER_KEY = 'parking-tycoon:serverUrl';
const TOKEN_KEY = 'parking-tycoon:token';
const USER_KEY = 'parking-tycoon:user';
// Default to same-origin ('') so that when the server serves the built client,
// /api/* just works. For separate-port local dev, set the URL in the Online
// panel (e.g. http://localhost:3001).
const DEFAULT_SERVER = '';

export interface SaveSlotInfo {
  slot: number;
  name: string;
  companyName: string;
  companyValue: number;
  updatedAt: string;
}
export interface LeaderRow {
  username: string;
  companyName: string;
  companyValue: number;
}
export interface PublicLots {
  companyName: string;
  companyValue: number;
  lots: Array<{
    name: string;
    districtId: string;
    price: number;
    reputation: number;
    revenue: number;
  }>;
}

/**
 * Normalize a user-entered server URL into an origin the fetch layer can safely
 * prefix onto `/api/...`, or '' for same-origin. Guards against the common
 * mistakes that break the deployed (same-origin) site:
 *  - missing scheme ("tycoon.pssgroup.app")   -> add https:// so it's absolute,
 *    not resolved relative to the page (which yields 404s / HTML from the SPA).
 *  - a trailing path/slash                     -> keep only the origin.
 *  - the page's own origin                     -> '' (relative same-origin calls).
 *  - anything malformed                        -> '' (fall back to same-origin).
 */
export function normalizeServerUrl(url: string): string {
  let v = (url ?? '').trim().replace(/\/+$/, '');
  if (!v) return '';
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
  try {
    const u = new URL(v);
    if (typeof location !== 'undefined' && u.origin === location.origin) return '';
    return u.origin;
  } catch {
    return '';
  }
}

export function getServerUrl(): string {
  return normalizeServerUrl(storage.get(SERVER_KEY) || DEFAULT_SERVER);
}
export function setServerUrl(url: string): void {
  const v = normalizeServerUrl(url);
  if (v) storage.set(SERVER_KEY, v);
  else storage.remove(SERVER_KEY);
}
export function getToken(): string | null {
  return storage.get(TOKEN_KEY);
}
export function getUsername(): string | null {
  return storage.get(USER_KEY);
}
export function isLoggedIn(): boolean {
  return !!getToken();
}
export function logout(): void {
  storage.remove(TOKEN_KEY);
  storage.remove(USER_KEY);
}

async function req<T>(
  path: string,
  opts: { method?: string; body?: unknown; auth?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await fetch(`${getServerUrl()}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new Error('offline'); // server unreachable (e.g. in the artifact)
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error((data as { error?: string }).error || `error ${res.status}`);
  return data;
}

export const api = {
  async register(username: string, password: string): Promise<void> {
    const r = await req<{ token: string; username: string }>('/api/register', {
      method: 'POST',
      body: { username, password },
    });
    storage.set(TOKEN_KEY, r.token);
    storage.set(USER_KEY, r.username);
  },
  async login(username: string, password: string): Promise<void> {
    const r = await req<{ token: string; username: string }>('/api/login', {
      method: 'POST',
      body: { username, password },
    });
    storage.set(TOKEN_KEY, r.token);
    storage.set(USER_KEY, r.username);
  },
  listSaves(): Promise<SaveSlotInfo[]> {
    return req<SaveSlotInfo[]>('/api/saves', { auth: true });
  },
  saveSlot(slot: number, name: string, data: unknown): Promise<{ ok: boolean }> {
    return req(`/api/saves/${slot}`, { method: 'PUT', auth: true, body: { name, data } });
  },
  loadSlot(slot: number): Promise<{ data: unknown }> {
    return req(`/api/saves/${slot}`, { auth: true });
  },
  leaderboard(): Promise<LeaderRow[]> {
    return req<LeaderRow[]>('/api/leaderboard');
  },
  viewLots(username: string): Promise<PublicLots> {
    return req<PublicLots>(`/api/lots/${encodeURIComponent(username)}`);
  },
};
